// MachineUtilizationDashboard/components/MachineStatusDonut.jsx

import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";

import { formatNumber } from "../utils/machineDashboardUtils";
import Loader from "../../../../../../common/Loader";

const STATUS_CONFIG = Object.freeze([
  {
    key: "running",
    label: "Running",
    color: "#16a34a",
    lightColor: "#dcfce7",
  },
  {
    key: "idle",
    label: "Idle",
    color: "#f59e0b",
    lightColor: "#fef3c7",
  },
  {
    key: "stopped",
    label: "Stopped",
    color: "#ef4444",
    lightColor: "#fee2e2",
  },
]);

const DEFAULT_ROTATION_INTERVAL = 3000;

const toSafeCount = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number) || number < 0) {
    return 0;
  }

  return Math.round(number);
};

function MachineStatusDonut({
  data = null,
  onStatusClick,
  autoRotate = true,
  autoRotateInterval = DEFAULT_ROTATION_INTERVAL,
}) {
  const runningMachines = toSafeCount(data?.runningMachines);
  const idleMachines = toSafeCount(data?.idleMachines);
  const stoppedMachines = toSafeCount(data?.stoppedMachines);
  const apiTotalMachines = toSafeCount(data?.totalMachines);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const intervalRef = useRef(null);

  const statusValues = useMemo(
    () => [runningMachines, idleMachines, stoppedMachines],
    [runningMachines, idleMachines, stoppedMachines],
  );

  const calculatedTotal = useMemo(
    () => statusValues.reduce((sum, value) => sum + value, 0),
    [statusValues],
  );

  const totalMachines =
    apiTotalMachines > 0 ? apiTotalMachines : calculatedTotal;

  const hasData = calculatedTotal > 0;

  const statusData = useMemo(
    () =>
      STATUS_CONFIG.map((status, index) => ({
        ...status,
        value: statusValues[index],
      })),
    [statusValues],
  );

  const chartSeries = useMemo(() => {
    if (!hasData) {
      return [1];
    }

    return [
      Number(runningMachines) || 0,
      Number(idleMachines) || 0,
      Number(stoppedMachines) || 0,
    ];
  }, [hasData, runningMachines, idleMachines, stoppedMachines]);

  /*
   * Keep ApexCharts options intentionally static.
   * Do not place React callbacks, API objects, refs, chart instances,
   * or changing data inside this object.
   */
  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "machine-status-donut",
        type: "donut",
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },

      labels: ["Running", "Idle", "Stopped"],

      colors: ["#16a34a", "#f59e0b", "#ef4444"],

      dataLabels: {
        enabled: false,
      },

      legend: {
        show: false,
      },

      stroke: {
        show: true,
        width: 1.3,
        colors: ["#ffffffa9"],
      },

      fill: {
        type: "solid",
      },

      tooltip: {
        enabled: true,
        y: {
          formatter: (value) => {
            const number = Number(value);

            return `${
              Number.isFinite(number) ? Math.round(number) : 0
            } machines`;
          },
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "none",
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: "none",
          },
        },
      },

      plotOptions: {
        pie: {
          expandOnClick: false,
          customScale: 0.92,

          donut: {
            size: "76%",
            labels: {
              show: false,
            },
          },
        },
      },
    }),
    [],
  );

  const getPercentage = (value) => {
    if (totalMachines <= 0) {
      return 0;
    }

    return Number(((toSafeCount(value) / totalMachines) * 100).toFixed(1));
  };

  const selectStatus = (index) => {
    if (
      !hasData ||
      !Number.isInteger(index) ||
      index < 0 ||
      index >= statusData.length
    ) {
      return;
    }

    setActiveIndex(index);

    const selectedStatus = statusData[index];

    if (typeof onStatusClick === "function") {
      onStatusClick({
        status: selectedStatus.key,
        label: selectedStatus.label,
        value: selectedStatus.value,
        percentage: getPercentage(selectedStatus.value),
        index,
      });
    }
  };

  useEffect(() => {
    if (!hasData || activeIndex >= STATUS_CONFIG.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, hasData]);

  useEffect(() => {
    if (data === null) {
      return (
        <section className="production-dashboard-section">
          <div className="production-dashboard-card loading-card">
            Loading production dashboard...
            <Loader isLoading={true} className={"text-center"} />
          </div>
        </section>
      );
    }

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const requestedInterval = Number(autoRotateInterval);
    const safeInterval = Number.isFinite(requestedInterval)
      ? Math.max(requestedInterval, 1000)
      : DEFAULT_ROTATION_INTERVAL;

    if (!autoRotate || !hasData || isManuallyPaused || isHovering) {
      return undefined;
    }

    intervalRef.current = window.setInterval(() => {
      setActiveIndex(
        (previousIndex) => (previousIndex + 1) % STATUS_CONFIG.length,
      );
    }, safeInterval);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRotate, autoRotateInterval, hasData, isHovering, isManuallyPaused, data]);

  const activeStatus = statusData[activeIndex] || statusData[0];

  return (
    <div
      className="mud-machine-status"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="mud-donut-layout">
        <div className="mud-donut-chart-wrapper">
          <div
            className="mud-donut-glow"
            style={{
              backgroundColor: hasData
                ? activeStatus?.color || "#2f539d"
                : "#cbd5e1",
            }}
          />

          {hasData ? (
            <Chart
              key={`machine-donut-${runningMachines}-${idleMachines}-${stoppedMachines}`}
              options={chartOptions}
              series={chartSeries}
              type="donut"
              height={570}
              width="100%"
            />
          ) : (
            <Chart
              key="machine-donut-empty"
              options={chartOptions}
              series={[1]}
              type="donut"
              height={570}
              width="100%"
            />
          )}

          <div className="mud-donut-center-content">
            <div
              className="mud-donut-center-content__status"
              style={{
                color: hasData ? activeStatus?.color || "#64748b" : "#64748b",
              }}
            >
              <span
                className="mud-donut-center-content__dot"
                style={{
                  backgroundColor: hasData
                    ? activeStatus?.color || "#64748b"
                    : "#94a3b8",
                  boxShadow: `0 0 0 4px ${
                    hasData ? activeStatus?.lightColor || "#f1f5f9" : "#f1f5f9"
                  }`,
                }}
              />

              <span>
                {hasData ? activeStatus?.label || "Machines" : "No Data"}
              </span>
            </div>

            <strong
              className="mud-donut-center-content__value"
              style={{
                color: hasData ? activeStatus?.color || "#0f172a" : "#64748b",
                fontSize: "16px",
                fontWeight: 650,
              }}
            >
              {formatNumber(hasData ? activeStatus?.value || 0 : 0)}
            </strong>

            <div className="mud-donut-center-content__percentage">
              <span>{hasData ? getPercentage(activeStatus?.value) : 0}%</span>
              <small>of total</small>
            </div>
          </div>
        </div>

        <div className="mud-status-content">
          <div className="mud-status-content__header">
            <div className="mud-status-content__summary">
              <span className="mud-status-content__eyebrow">
                Live Machine Status
              </span>

              <h4>
                {formatNumber(totalMachines)}
                <small>Total Machines</small>
              </h4>
            </div>

            {autoRotate && (
              <button
                type="button"
                className={`mud-auto-rotate ${
                  isManuallyPaused || !hasData ? "is-paused" : "is-playing"
                }`}
                disabled={!hasData}
                onClick={() => {
                  setIsManuallyPaused((previousValue) => !previousValue);
                }}
              >
                <span className="mud-auto-rotate__indicator" />
                {!hasData ? "No Data" : isManuallyPaused ? "Paused" : "Live"}
              </button>
            )}
          </div>

          <div className="mud-status-legend">
            {statusData.map((item, index) => {
              const isActive = hasData && activeIndex === index;
              const percentage = getPercentage(item.value);

              return (
                <button
                  type="button"
                  key={item.key}
                  className={`mud-status-legend__item ${
                    isActive ? "is-active" : ""
                  }`}
                  style={{
                    "--status-color": item.color,
                    "--status-light": item.lightColor,
                  }}
                  disabled={!hasData}
                  onClick={() => selectStatus(index)}
                >
                  <div className="mud-status-legend__top">
                    <span className="mud-status-legend__icon">
                      <span className="mud-status-legend__dot" />
                    </span>

                    <div className="mud-status-legend__label">
                      <small>{item.label}</small>
                      <strong>{formatNumber(item.value)}</strong>
                    </div>

                    <div className="mud-status-legend__percentage">
                      {percentage}%
                    </div>
                  </div>

                  <div className="mud-status-legend__progress">
                    <span
                      style={{
                        width: `${Math.min(percentage, 100)}%`,
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(MachineStatusDonut);
